import React from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Tooltip, Typography } from "@material-ui/core";
import CodeIcon from "@material-ui/icons/Code";
import VisibilityIcon from "@material-ui/icons/Visibility";
import GetAppIcon from "@material-ui/icons/GetApp";
import DeleteIcon from "@material-ui/icons/Delete";
import { useMaterialSpecs } from "pages/MaterialSpecs/hooks";
import { Button, ConfirmationDialog, PermissionWrapper } from "components";
import { useConfirmationDialog } from "hooks";
import { CreativeToolbarContainer } from "pages/MaterialSpecs/components/CreativesSection/CreativeToolbar/CreativeToolbar.styled";
import { Permission } from "api";
import { useDownloadMaterials } from "hooks/api";

interface CreativeToolbarProps {
    printed: boolean;
    materialId: number;
    materialSpecId: number;
    isThirdPartyTagMaterial: boolean;
    isMaterialEmpty: boolean;
    permissions: Permission[];
}

export const CreativeToolbar = ({
    materialSpecId,
    materialId,
    printed,
    isThirdPartyTagMaterial,
    isMaterialEmpty,
    permissions,
}: CreativeToolbarProps) => {
    const { t } = useTranslation();
    const { refetch, isFetching } = useDownloadMaterials("AD_MATERIAL", materialId);
    const { remove, select, selectedMaterialId, setTab, setPreviewVisibility } = useMaterialSpecs();
    const {
        confirmationId,
        isConfirmationOpen,
        handleOnOpen,
        handleOnExited,
        handleOnConfirm,
        handleOnClose,
    } = useConfirmationDialog(() => {
        remove(materialSpecId, materialId);
        handleOnClose();
    });

    const handleTabSelect = () => {
        if ((isThirdPartyTagMaterial || isMaterialEmpty) && selectedMaterialId === materialId) {
            setTab("THIRD_PARTY_TAG");

            return;
        }

        select(
            printed,
            materialSpecId,
            materialId,
            isThirdPartyTagMaterial || isMaterialEmpty ? "THIRD_PARTY_TAG" : "CUSTOMER_SUMMARY"
        );
    };

    return (
        <>
            <ConfirmationDialog
                headerLabel={t("campaign.label.deleteMaterial")}
                isOpen={isConfirmationOpen}
                onClose={handleOnConfirm}
                onExited={handleOnExited}>
                <Typography variant="body2">
                    {t("campaign.warnings.deleteMaterialWarning", { id: confirmationId })}
                </Typography>
            </ConfirmationDialog>

            <Box display="flex" flex={1} justifyContent="space-between" alignItems="center">
                <CreativeToolbarContainer display="flex">
                    {!printed && (
                        <>
                            {isThirdPartyTagMaterial || isMaterialEmpty ? (
                                <PermissionWrapper name="UPLOAD_ADMATERIAL" permissions={permissions}>
                                    {({ disabled, tooltip }) => (
                                        <Tooltip title={tooltip}>
                                            <span>
                                                <IconButton disableRipple onClick={handleTabSelect} disabled={disabled}>
                                                    <CodeIcon color={disabled ? "disabled" : "inherit"} />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                </PermissionWrapper>
                            ) : (
                                <Tooltip title={t("campaign.info.not3rdPartyInfo") || ""}>
                                    <span>
                                        <IconButton disableRipple disabled>
                                            <CodeIcon color="disabled" />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            )}
                        </>
                    )}

                    {!isMaterialEmpty && (
                        <IconButton
                            disableRipple
                            onClick={() => setPreviewVisibility(true, materialSpecId, materialId)}>
                            <VisibilityIcon color="inherit" />
                        </IconButton>
                    )}

                    <PermissionWrapper name="DOWNLOAD" permissions={permissions}>
                        {({ disabled, tooltip }) => (
                            <Tooltip title={tooltip}>
                                <span>
                                    <IconButton
                                        disableRipple
                                        disabled={disabled || isFetching}
                                        onClick={() => refetch()}>
                                        <GetAppIcon color={disabled ? "disabled" : "inherit"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </PermissionWrapper>

                    <PermissionWrapper name="DELETE" permissions={permissions}>
                        {({ disabled, tooltip }) => (
                            <Tooltip title={tooltip}>
                                <span>
                                    <IconButton disableRipple onClick={handleOnOpen(materialId)} disabled={disabled}>
                                        <DeleteIcon color={disabled ? "disabled" : "inherit"} />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </PermissionWrapper>
                </CreativeToolbarContainer>

                <PermissionWrapper name="REVIEW" permissions={permissions}>
                    {({ disabled, tooltip }) => (
                        <Tooltip title={tooltip}>
                            <span>
                                <Button
                                    p={0}
                                    disableRipple
                                    variant="outlined"
                                    disabled={disabled}
                                    onClick={() => setPreviewVisibility(true, materialSpecId, materialId)}
                                    color="primary">
                                    {t("buttons.label.review")}
                                </Button>
                            </span>
                        </Tooltip>
                    )}
                </PermissionWrapper>
            </Box>
        </>
    );
};
